import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    AlertTitle,
    Box,
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNotification } from "../../../context/NotificationContext";
import ProjectService from "../../../services/ProjectService";

const DOMAIN_PATTERN =
    /^(?=.{1,255}$)(?!-)(?:[a-zA-Z0-9-]{1,63}\.)+[A-Za-z]{2,63}$/;

const normalizeDomain = (value) =>
    typeof value === "string"
        ? value.trim().replace(/^@+/, "").toLowerCase()
        : "";

const TestCaseConfigTab = ({ projectId, initialEmailDomain, onSaved }) => {
    const showNotification = useNotification();
    const [emailDomain, setEmailDomain] = useState(initialEmailDomain || "");
    const [savedEmailDomain, setSavedEmailDomain] = useState(
        initialEmailDomain || ""
    );
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    useEffect(() => {
        const nextValue = initialEmailDomain || "";
        setEmailDomain(nextValue);
        setSavedEmailDomain(nextValue);
    }, [initialEmailDomain]);

    useEffect(() => {
        if (!isSaved) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setIsSaved(false);
        }, 2000);

        return () => window.clearTimeout(timeoutId);
    }, [isSaved]);

    const normalizedEmailDomain = useMemo(
        () => normalizeDomain(emailDomain),
        [emailDomain]
    );
    const normalizedSavedEmailDomain = useMemo(
        () => normalizeDomain(savedEmailDomain),
        [savedEmailDomain]
    );
    const isDirty = normalizedEmailDomain !== normalizedSavedEmailDomain;
    const isValidDomain = DOMAIN_PATTERN.test(normalizedEmailDomain);
    const showValidationError =
        normalizedEmailDomain.length > 0 && !isValidDomain;

    const handleCancel = () => {
        setEmailDomain(savedEmailDomain);
        setIsSaved(false);
    };

    const handleSave = async () => {
        if (!isDirty || !isValidDomain) {
            return;
        }

        setIsSaving(true);
        setIsSaved(false);

        try {
            const updatedProject = await ProjectService.updateProjectTestCaseConfig(
                projectId,
                {
                    testCaseEmailDomain: normalizedEmailDomain,
                }
            );

            setEmailDomain(updatedProject.test_case_email_domain);
            setSavedEmailDomain(updatedProject.test_case_email_domain);
            setIsSaved(true);
            onSaved?.(updatedProject);
            showNotification("Email domain updated", "success", "");
        } catch (error) {
            showNotification(
                error.response?.data?.error || "Failed to update email domain",
                "error",
                ""
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Alert className="projectSettingsAlert" severity="info">
                <AlertTitle className="projectSettingsAlertTitle">
                    Important
                </AlertTitle>
                <span>
                    Changes here affect future test runs that generate random data for this project.
                </span>
            </Alert>
            <Box className="projectSettingsTextItemContainer">
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                    }}
                >
                    <Typography sx={{ textTransform: "lowercase" }}>
                        email domain
                    </Typography>
                    <Tooltip
                        title="Set the email domain (e.g., gmail.com) to use for randomly generated email addresses in test cases."
                    >
                        <IconButton size="small" sx={{ p: 0 }}>
                            <InfoOutlinedIcon
                                fontSize="small"
                                color="primary"
                            />
                        </IconButton>
                    </Tooltip>
                </Box>
                <TextField
                    className="projectSettingsTextItem"
                    placeholder="gmail.com"
                    value={emailDomain}
                    onChange={(event) => {
                        setEmailDomain(event.target.value);
                        setIsSaved(false);
                    }}
                    error={showValidationError}
                    helperText={
                        showValidationError
                            ? "Enter a valid domain like gmail.com"
                            : isDirty
                              ? "Unsaved changes"
                              : isSaved
                                ? "Saved"
                                : " "
                    }
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {isSaving ? (
                                    <CircularProgress size={18} />
                                ) : (
                                    <>
                                        {isDirty ? (
                                            <>
                                                <IconButton
                                                    aria-label="cancel email domain changes"
                                                    edge="end"
                                                    onClick={handleCancel}
                                                    size="small"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    aria-label="save email domain"
                                                    edge="end"
                                                    onClick={handleSave}
                                                    size="small"
                                                    color="primary"
                                                    disabled={!isValidDomain}
                                                >
                                                    <CheckIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        ) : null}
                                        {!isDirty && isSaved ? (
                                            <CheckIcon
                                                fontSize="small"
                                                color="success"
                                            />
                                        ) : null}
                                    </>
                                )}
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
        </>
    );
};

export default TestCaseConfigTab;
